/**
 * A compact "focus" dropdown — a pill showing the current choice that
 * opens a small menu of options. Used in the Grow header to narrow the
 * screen to a single section (Memory Verse / Sermon Notes / Go Deeper /
 * Prayer / …) or back to "All".
 *
 * Closes on select, on outside tap (a transparent full-screen catcher),
 * and on Escape. Single-select with a check on the active row
 * (role="menuitemradio").
 */
import { useEffect, useState } from 'react';

export interface FocusOption {
  key: string;
  label: string;
}

interface Props {
  value: string;
  options: readonly FocusOption[];
  onChange: (key: string) => void;
  /** Accessible name for the trigger. */
  label?: string;
}

export function FocusMenu({ value, options, onChange, label = 'Focus' }: Props) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const current = options.find((o) => o.key === value) ?? options[0];

  return (
    <div className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-10 items-center gap-1.5 rounded-full border border-line bg-card pl-4 pr-3 text-sm font-medium text-ink"
      >
        {current?.label}
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-soft" fill="none" aria-hidden>
          <path
            d="m6 9 6 6 6-6"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <>
          {/* Transparent catcher so any outside tap closes the menu. */}
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-20 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div role="menu" className="card absolute right-0 z-30 mt-1 w-48 p-1">
            {options.map((o) => {
              const active = o.key === value;
              return (
                <button
                  key={o.key}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onChange(o.key);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-line/60 ${
                    active ? 'text-oxblood' : 'text-ink'
                  }`}
                >
                  {o.label}
                  {active && (
                    <svg
                      viewBox="0 0 24 24"
                      className="h-4 w-4 shrink-0"
                      fill="none"
                      aria-hidden
                    >
                      <path
                        d="m5 13 4 4L19 7"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
